import React, { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Wrench, Phone, Home, Hammer, Clock, CheckCircle2, ArrowRight, ChevronDown, Drill, Paintbrush, Ruler, Scissors, Zap, Star, Layers, Building2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const services = [{
  id: 'maintenance',
  icon: <Wrench className="h-10 w-10" data-id="element-252" data-mp="2g8msiv8jbpmz" />,
  title: 'Maintenance & Repairs',
  shortDescription: 'From leaky faucets to drywall patches, we handle the small fixes that keep your home running smoothly.',
  timeframe: '1-2 days typical turnaround',
  color: 'bg-blue-50 text-blue-700',
  badge: 'Most Popular',
  badgeColor: 'bg-blue-600',
  details: {
    overview: 'Regular maintenance and timely repairs are essential to keeping your home in top condition. We specialize in the everyday fixes that bigger contractors often overlook.',
    services: ['Plumbing repairs (leaky faucets, running toilets, drain issues)', 'Drywall repair and patching', 'Door and window adjustments', 'Caulking and weatherstripping', 'Minor electrical fixes (outlet replacement, switch repair)', 'Painting touch-ups and small paint jobs', 'Gutter cleaning and minor repairs', 'Deck and fence maintenance'],
    process: 'We start with a thorough assessment, provide a clear estimate, and complete most repairs in a single visit. Our goal is to fix it right the first time.',
    pricing: 'Hourly rates or flat-fee pricing depending on the scope. Free estimates for larger projects.'
  }
}, {
  id: 'emergency',
  icon: <Phone className="h-10 w-10" data-id="element-253" data-mp="2wxexpemcqsy" />,
  title: 'Emergency Service Calls',
  shortDescription: 'Something break unexpectedly? We offer quick diagnostic visits and immediate repairs for urgent issues.',
  timeframe: 'Same or next day response',
  color: 'bg-red-50 text-red-700',
  badge: 'Fast Response',
  badgeColor: 'bg-red-600',
  details: {
    overview: 'When something breaks unexpectedly, you need a contractor who responds quickly and fixes the problem right. We prioritize emergency calls and offer same-day or next-day service.',
    services: ['Burst pipe emergency response', 'Water leak detection and repair', 'Electrical emergencies (no power, sparking outlets)', 'Broken window or door security issues', 'HVAC system failures', 'Roof leak emergency patching', 'Appliance hookup emergencies', 'Storm damage assessment and temporary repairs'],
    process: "Call us immediately and we'll assess the urgency. For true emergencies, we can often be on-site within hours. We'll stabilize the situation and provide a plan for permanent repairs.",
    pricing: "Emergency service fees apply for same-day calls. We'll always provide pricing before starting work."
  }
}, {
  id: 'remodels',
  icon: <Home className="h-10 w-10" data-id="element-254" data-mp="x3e8p7x0c7j7" />,
  title: 'Remodels',
  shortDescription: 'Bathroom updates, kitchen refreshes, and room transformations. We specialize in high-impact renovation projects.',
  timeframe: '1-4 week project duration',
  color: 'bg-green-50 text-green-700',
  badge: 'Transform Your Space',
  badgeColor: 'bg-green-600',
  details: {
    overview: 'Transform your space with professional remodeling services. From single-room updates to multi-room renovations, we handle projects of all sizes with attention to detail and quality craftsmanship.',
    services: ['Bathroom remodels (full or partial updates)', 'Kitchen renovations and upgrades', 'Basement finishing and conversions', 'Room additions and expansions', 'Laundry room organization and upgrades', 'Closet system installation and custom storage', 'Home office buildouts', 'Garage conversions and organization systems'],
    process: "We'll meet with you to understand your vision, provide design suggestions, create a detailed estimate, and complete the work with minimal disruption to your daily routine. We handle permits, inspections, and all aspects of the project.",
    pricing: "Project-based pricing with detailed line-item estimates. We'll work within your budget and suggest cost-effective alternatives when needed. Financing options available for larger projects."
  }
}, {
  id: 'handyman',
  icon: <Hammer className="h-10 w-10" data-id="element-255" data-mp="3aam9d1ew3qcl" />,
  title: 'Handyman Services',
  shortDescription: 'TV mounting, furniture assembly, door adjustments, and honey-do lists. No job is too small.',
  timeframe: 'Hourly rates available',
  color: 'bg-orange-50 text-orange-700',
  badge: 'Quick Tasks',
  badgeColor: 'bg-orange-600',
  details: {
    overview: 'Sometimes you just need an extra pair of skilled hands. Our handyman services cover all those tasks that pile up on your to-do list but never seem to get done.',
    services: ['TV and picture mounting', 'Furniture assembly (IKEA, etc.)', 'Shelf and storage installation', 'Light fixture replacement', 'Ceiling fan installation', 'Smart home device installation', 'Minor carpentry work', 'Honey-do list completion'],
    process: "Send us your list and we'll give you a time estimate. Most handyman tasks can be completed in a few hours. We bring all necessary tools and hardware.",
    pricing: 'Hourly rates with a minimum service call. We can often tackle multiple small tasks in a single visit to maximize value.'
  }
}, {
  id: 'flooring',
  icon: <Layers className="h-10 w-10" data-id="element-256" data-mp="3ogfvu73adeod" />,
  title: 'Flooring Installation',
  shortDescription: 'Expert installation of all flooring types including hardwood, laminate, vinyl, tile, and carpet. Professional subfloor prep included.',
  timeframe: '2-5 days typical project',
  color: 'bg-purple-50 text-purple-700',
  badge: 'New Service',
  badgeColor: 'bg-purple-600',
  details: {
    overview: 'Upgrade your home with beautiful new flooring. We provide professional installation for all major flooring types, ensuring a durable and flawless finish that transforms your space.',
    services: ['Hardwood flooring installation and refinishing', 'Laminate and vinyl plank (LVP) installation', 'Tile flooring (ceramic, porcelain, stone)', 'Carpet installation and removal', 'Subfloor repair and leveling', 'Baseboard and trim installation', 'Old flooring removal and disposal', 'Stair tread and riser installation'],
    process: "We start with a free measurement and estimate. Once materials are selected, we handle everything from furniture moving and demo to installation and cleanup. We don't leave until you're 100% satisfied.",
    pricing: 'Project-based pricing per square foot. We provide detailed estimates that include labor, materials (if provided by us), and disposal fees.'
  }
}, {
  id: 'commercial-cleaning',
  icon: <Building2 className="h-10 w-10" data-id="element-257" data-mp="1qtzqibwxl06a" />,
  title: 'Commercial Cleaning',
  shortDescription: 'Professional cleaning services for offices, retail spaces, and commercial properties. We keep your business looking its best.',
  timeframe: 'Daily, weekly, or monthly',
  color: 'bg-teal-50 text-teal-700',
  badge: 'New Service',
  badgeColor: 'bg-teal-600',
  details: {
    overview: 'Maintain a pristine environment for your employees and customers with our professional commercial cleaning services. We offer flexible schedules and customized cleaning plans to meet the unique needs of your business.',
    services: ['Office cleaning and sanitization', 'Retail store cleaning', 'Restroom cleaning and restocking', 'Floor care (vacuuming, mopping, buffing)', 'Window cleaning (interior and exterior)', 'Trash removal and recycling', 'Breakroom and kitchen cleaning', 'Post-construction cleaning'],
    process: 'We begin with a walkthrough of your facility to understand your specific needs. Then, we create a customized cleaning schedule and checklist. Our team arrives on time, fully equipped, and follows strict quality control protocols.',
    pricing: 'Custom monthly contracts based on square footage and frequency of service. One-time deep cleans are also available.'
  }
}];
const floatingTools = [{
  icon: Wrench,
  delay: 0,
  x: '10%',
  y: '15%',
  rotation: -15,
  size: 80
}, {
  icon: Hammer,
  delay: 0.2,
  x: '85%',
  y: '20%',
  rotation: 25,
  size: 70
}, {
  icon: Drill,
  delay: 0.4,
  x: '15%',
  y: '75%',
  rotation: -20,
  size: 90
}, {
  icon: Paintbrush,
  delay: 0.6,
  x: '80%',
  y: '70%',
  rotation: 15,
  size: 75
}, {
  icon: Ruler,
  delay: 0.8,
  x: '50%',
  y: '10%',
  rotation: -10,
  size: 85
}, {
  icon: Scissors,
  delay: 1,
  x: '90%',
  y: '50%',
  rotation: 30,
  size: 65
}];
export function ServicesPage() {
  const [expandedService, setExpandedService] = useState<string | null>(null);
  const [hoveredService, setHoveredService] = useState<string | null>(null);
  const toggleService = (id: string) => {
    setExpandedService(expandedService === id ? null : id);
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-258" data-mp="4g0de6c8u3uv">
      <Navbar data-id="element-259" data-mp="2u40xshw7ldzz" />

      <main className="flex-grow pt-24" data-id="element-260" data-mp="2u4sqnkyf8xmk">
        {/* Enhanced Hero Section with Floating Tools */}
        <div className="relative overflow-hidden bg-gradient-to-br from-white via-white to-[#17a2b8]/5 py-12 md:py-16" data-id="element-261" data-mp="2p2tp8gm8sas9">
          {/* Animated Tool Icons Background */}
          <div className="absolute inset-0 pointer-events-none" data-id="element-262" data-mp="2r70kbwtwyer3">
            {floatingTools.map((tool, index) => {
            const Icon = tool.icon;
            return <motion.div key={index} className="absolute text-[#17a2b8]" style={{
              left: tool.x,
              top: tool.y,
              width: tool.size,
              height: tool.size
            }} initial={{
              opacity: 0,
              scale: 0,
              rotate: 0
            }} animate={{
              opacity: 0.08,
              scale: 1,
              rotate: tool.rotation,
              y: [0, -20, 0]
            }} transition={{
              opacity: {
                duration: 0.8,
                delay: tool.delay
              },
              scale: {
                duration: 0.8,
                delay: tool.delay
              },
              rotate: {
                duration: 0.8,
                delay: tool.delay
              },
              y: {
                duration: 4,
                delay: tool.delay + 1,
                repeat: Infinity,
                ease: 'easeInOut'
              }
            }} data-id="element-263" data-mp="313n4ejef11s2" data-mpi={index}>
                  <Icon className="w-full h-full" strokeWidth={1.5} data-id="element-264" data-mp="38g31sh3csafn" />
                </motion.div>;
          })}
          </div>

          {/* Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-265" data-mp="16nx1fn7vmzgr">
            <div className="text-center max-w-2xl mx-auto relative z-10" data-id="element-266" data-mp="16opz7youyemr">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} data-id="element-267" data-mp="1blq8sjkx1zim">
                <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.5,
                delay: 0.2
              }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#17a2b8]/10 text-[#17a2b8] text-sm font-semibold mb-4 border border-[#17a2b8]/20" data-id="element-268" data-mp="107y84dl0h7wv">
                  <Wrench className="h-4 w-4" data-id="element-269" data-mp="2bnvb35popsd3" />
                  Professional Services
                </motion.div>

                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2c2c2c] mb-4" data-id="element-270" data-mp="26uk1wxxegzi0">
                  Our <span className="text-[#17a2b8]" data-id="element-271" data-mp="3pkftlxguchuo">Services</span>
                </h1>
                <p className="text-lg text-[#525252] mb-6 leading-relaxed" data-id="element-272" data-mp="3uizkmcaakjtw">
                  Professional maintenance, repairs, and remodeling services for
                  Portland homes and businesses.
                </p>
                <Button variant="primary" size="lg" onClick={() => document.getElementById('services-detail')?.scrollIntoView({
                behavior: 'smooth'
              })} data-id="element-273" data-mp="7ejnztiieckk">
                  Explore Services
                </Button>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Detailed Services Section with Enhanced Animations */}
        <Section id="services-detail" background="default" data-id="element-274" data-mp="v0q473236ylv">
          <div className="space-y-6" data-id="element-275" data-mp="102z0o9e64isq">
            {services.map((service, index) => <motion.div key={service.id} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: index * 0.15,
            ease: 'easeOut'
          }} onHoverStart={() => setHoveredService(service.id)} onHoverEnd={() => setHoveredService(null)} className="relative" data-id="element-276" data-mp="mef4x6c6oex1" data-mpi={index}>
                <motion.div animate={{
              scale: hoveredService === service.id ? 1.02 : 1,
              y: hoveredService === service.id ? -4 : 0
            }} transition={{
              duration: 0.2
            }} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden relative" data-id="element-277" data-mp="8ee0xi0mgrdc">
                  {/* Badge */}
                  {service.badge && <motion.div initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.15 + 0.3
              }} className={`absolute top-4 right-4 ${service.badgeColor} text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 z-10`} data-id="element-278" data-mp="1iegt0w2wep2j">
                      <Star className="h-3 w-3" data-id="element-279" data-mp="2xupfgxo773sn" />
                      {service.badge}
                    </motion.div>}

                  {/* Service Header - Always Visible */}
                  <button onClick={() => toggleService(service.id)} className="w-full p-8 flex items-start gap-6 hover:bg-gray-50/50 transition-colors text-left" data-id="element-280" data-mp="33o1bj3mrewyp">
                    <motion.div className={`w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 ${service.color} shadow-sm`} whileHover={{
                  rotate: [0, -10, 10, -10, 0],
                  scale: 1.1
                }} transition={{
                  duration: 0.5
                }} data-id="element-281" data-mp="2tpbt99qybahx">
                      {service.icon}
                    </motion.div>

                    <div className="flex-grow" data-id="element-282" data-mp="25zhgstajnny7">
                      <div className="flex items-start justify-between gap-4 mb-3" data-id="element-283" data-mp="211pmxtqc9rrd">
                        <h3 className="text-2xl font-bold text-[#2c2c2c] group-hover:text-[#17a2b8] transition-colors" data-id="element-284" data-mp="21422y1cjds1s">
                          {service.title}
                        </h3>
                        <motion.div animate={{
                      rotate: expandedService === service.id ? 180 : 0
                    }} transition={{
                      duration: 0.3
                    }} className="flex-shrink-0" data-id="element-285" data-mp="1vyyn6uexveii">
                          <div className="w-8 h-8 rounded-full bg-[#17a2b8]/10 flex items-center justify-center" data-id="element-286" data-mp="1d75vd3blvzcd">
                            <ChevronDown className="h-5 w-5 text-[#17a2b8]" data-id="element-287" data-mp="1i45zh8owas0u" />
                          </div>
                        </motion.div>
                      </div>

                      <p className="text-[#525252] mb-4 leading-relaxed" data-id="element-288" data-mp="pfqoqycsqvvb">
                        {service.shortDescription}
                      </p>

                      <div className="flex items-center gap-2 text-sm font-medium text-[#17a2b8] bg-[#17a2b8]/10 px-4 py-2 rounded-full w-fit border border-[#17a2b8]/20" data-id="element-289" data-mp="udjujqa6tnv2">
                        <Clock className="h-4 w-4" data-id="element-290" data-mp="24mklckejvsb0" />
                        {service.timeframe}
                      </div>
                    </div>
                  </button>

                  {/* Expandable Details with Smooth Animation */}
                  <AnimatePresence data-id="element-291" data-mp="2rkz1mutyunt9">
                    {expandedService === service.id && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.4,
                  ease: 'easeInOut'
                }} className="overflow-hidden" data-id="element-292" data-mp="2njvjg1og4fov">
                        <div className="px-8 pb-8 pt-4 border-t border-gray-100 bg-gradient-to-b from-gray-50/50 to-transparent" data-id="element-293" data-mp="3tve48fnyiwbp">
                          <motion.div initial={{
                      y: -20,
                      opacity: 0
                    }} animate={{
                      y: 0,
                      opacity: 1
                    }} transition={{
                      delay: 0.1
                    }} className="space-y-6" data-id="element-294" data-mp="3kwanhic9it0d">
                            {/* Overview */}
                            <div data-id="element-295" data-mp="2nbecxgjlqu1x">
                              <h4 className="text-lg font-bold text-[#2c2c2c] mb-3 flex items-center gap-2" data-id="element-296" data-mp="2s8eg9jp2hipc">
                                <div className="w-1 h-6 bg-[#17a2b8] rounded-full" data-id="element-297" data-mp="2rbprq456ydlb"></div>
                                Overview
                              </h4>
                              <p className="text-[#525252] leading-relaxed" data-id="element-298" data-mp="2hel6c6j66m5k">
                                {service.details.overview}
                              </p>
                            </div>

                            {/* Services List with Staggered Animation */}
                            <div data-id="element-299" data-mp="3a5bqzcqyoh0i">
                              <h4 className="text-lg font-bold text-[#2c2c2c] mb-3 flex items-center gap-2" data-id="element-300" data-mp="3f2v5mdrkmvsz">
                                <div className="w-1 h-6 bg-[#17a2b8] rounded-full" data-id="element-301" data-mp="30e7dkhgxp47s"></div>
                                What We Do
                              </h4>
                              <div className="grid sm:grid-cols-2 gap-3" data-id="element-302" data-mp="3t238phquuo2g">
                                {service.details.services.map((item, idx) => <motion.div key={idx} initial={{
                            opacity: 0,
                            x: -10
                          }} animate={{
                            opacity: 1,
                            x: 0
                          }} transition={{
                            delay: 0.2 + idx * 0.05
                          }} className="flex items-start gap-2 group" data-id="element-303" data-mp="1o772z73jzsv" data-mpi={idx}>
                                    <CheckCircle2 className="h-5 w-5 text-[#17a2b8] flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" data-id="element-304" data-mp="1cdk50dw7tms9" />
                                    <span className="text-[#525252] text-sm" data-id="element-305" data-mp="2nu23sknac1i2">
                                      {item}
                                    </span>
                                  </motion.div>)}
                              </div>
                            </div>

                            {/* Process */}
                            <div data-id="element-306" data-mp="1lj8ihsi0asp4">
                              <h4 className="text-lg font-bold text-[#2c2c2c] mb-3 flex items-center gap-2" data-id="element-307" data-mp="1qfyv25ulr65s">
                                <div className="w-1 h-6 bg-[#17a2b8] rounded-full" data-id="element-308" data-mp="1uidear26o7xl"></div>
                                Our Process
                              </h4>
                              <p className="text-[#525252] leading-relaxed" data-id="element-309" data-mp="12qxmmilrrv0i">
                                {service.details.process}
                              </p>
                            </div>

                            {/* Pricing */}
                            <div data-id="element-310" data-mp="1bk15j6f6p02j">
                              <h4 className="text-lg font-bold text-[#2c2c2c] mb-3 flex items-center gap-2" data-id="element-311" data-mp="1bl2ixhjo9ynf">
                                <div className="w-1 h-6 bg-[#17a2b8] rounded-full" data-id="element-312" data-mp="j2x842o6ayd9"></div>
                                Pricing
                              </h4>
                              <p className="text-[#525252] leading-relaxed" data-id="element-313" data-mp="biw34ym2l1xx">
                                {service.details.pricing}
                              </p>
                            </div>

                            {/* CTA */}
                            <motion.div className="pt-4" initial={{
                        opacity: 0,
                        y: 10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} transition={{
                        delay: 0.4
                      }} data-id="element-314" data-mp="3tpapnlyemk7x">
                              <Button variant="primary" rightIcon={<ArrowRight className="h-4 w-4" data-id="element-316" data-mp="310vb10j6q3x4" />} onClick={() => {
                          window.location.href = '/#contact';
                        }} data-id="element-315" data-mp="35ywv9osck9a6">
                                Request This Service
                              </Button>
                            </motion.div>
                          </motion.div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>
              </motion.div>)}
          </div>
        </Section>

        {/* Enhanced CTA Section */}
        <Section background="primary" className="bg-gradient-to-br from-[#17a2b8] to-[#138496] relative overflow-hidden" data-id="element-317" data-mp="c9l1z70bierh">
          {/* Decorative elements */}
          <div className="absolute inset-0 opacity-10" data-id="element-318" data-mp="26mawnqyifrzx">
            <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-3xl" data-id="element-319" data-mp="26kretvx7h8w1"></div>
            <div className="absolute bottom-10 right-10 w-40 h-40 bg-white rounded-full blur-3xl" data-id="element-320" data-mp="2bjb2q1vd0ur1"></div>
          </div>

          <div className="text-center max-w-3xl mx-auto relative z-10" data-id="element-321" data-mp="3r4v6l9cvjt8d">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-322" data-mp="3r2igg8wyqck8">
              <motion.div initial={{
              scale: 0
            }} whileInView={{
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-6" data-id="element-323" data-mp="dp6vogzk7a1i">
                <Zap className="h-8 w-8 text-white" data-id="element-324" data-mp="3f4q0lst9tkh4" />
              </motion.div>

              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-325" data-mp="3j48ew5ql6xaz">
                Ready to Get Started?
              </h2>
              <p className="text-white/90 text-lg mb-8" data-id="element-326" data-mp="3o2s023xcw52a">
                Contact us today for a free estimate. We'll discuss your project
                and provide transparent pricing with no hidden fees.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-327" data-mp="5ygm9htyf4mc">
                <Button variant="secondary" size="lg" className="bg-white !text-orange-600 hover:bg-orange-50 shadow-xl border-2 border-orange-600 font-bold relative z-20" onClick={() => window.location.href = 'tel:5036068823'} data-id="element-328" data-mp="5wdna8atq15h">
                  Call (503) 606-8823
                </Button>
                <Button variant="secondary" size="lg" className="bg-white !text-orange-600 hover:bg-orange-50 shadow-xl border-2 border-orange-600 font-bold relative z-20" onClick={() => window.location.href = '/#contact'} data-id="element-329" data-mp="pmz2vb0w99pf">
                  Send a Message
                </Button>
              </div>
            </motion.div>
          </div>
        </Section>
      </main>

      <Footer data-id="element-330" data-mp="2svj31jmu4w2v" />
    </div>;
}